import React, { useEffect, useState } from "react";
import { Spinner, Tab } from "react-bootstrap";
import { FileEarmark, Search, X } from "react-bootstrap-icons";
import { filesService } from "../../services/files-service";
import * as Styled from "./VodLinksModal.styled";

interface IFileItem {
    itemId: string;
    fileId?: string;
    originalFilename?: string;
    objectSize?: number;
    mimeType?: string;
    downloadUrl?: string;
}

interface IHyperlinkItem {
    hyperLinkId: string;
    title: string;
    url: string;
}

interface VodLinksModalProps {
    show: boolean;
    mediaId: string;
    onHide: () => void;
}

export const VodLinksModal: React.FC<VodLinksModalProps> = ({ show, mediaId, onHide }) => {
    const [linkedFiles, setLinkedFiles] = useState<IFileItem[]>([]);
    const [linkedHyperlinks, setLinkedHyperlinks] = useState<IHyperlinkItem[]>([]);
    const [loading, setLoading] = useState(false);

    const [fileSearchQuery, setFileSearchQuery] = useState("");
    const [fileSearchResults, setFileSearchResults] = useState<IFileItem[]>([]);
    const [fileSearching, setFileSearching] = useState(false);

    const [hyperlinkUrl, setHyperlinkUrl] = useState("");
    const [hyperlinkTitle, setHyperlinkTitle] = useState("");
    const [addingLink, setAddingLink] = useState(false);

    useEffect(() => {
        if (show) {
            loadAttachments();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, mediaId]);

    async function loadAttachments() {
        setLoading(true);
        try {
            const attachmentsData = await filesService.getVODAttachments(mediaId);
            setLinkedFiles(attachmentsData?.files || []);
            setLinkedHyperlinks(attachmentsData?.hyperlinks || []);
        } catch (err) {
            console.error("Failed to load attachments", err);
        } finally {
            setLoading(false);
        }
    }

    async function handleFileSearch(e: React.ChangeEvent<HTMLInputElement>) {
        const q = e.target.value;
        setFileSearchQuery(q);
        if (!q.trim()) {
            setFileSearchResults([]);
            return;
        }
        setFileSearching(true);
        try {
            const results = await filesService.searchFiles(q);
            setFileSearchResults(results || []);
        } catch (err) {
            console.error("File search failed", err);
        } finally {
            setFileSearching(false);
        }
    }

    const linkedFileIds = new Set(linkedFiles.map((f) => f.fileId));

    async function handleLinkFile(fileId: string) {
        try {
            await filesService.linkFileToVOD(mediaId, fileId);
            await loadAttachments();
        } catch (err) {
            console.error("Failed to link file", err);
        }
    }

    async function handleUnlinkFile(fileId: string) {
        try {
            await filesService.unlinkFileFromVOD(mediaId, fileId);
            await loadAttachments();
        } catch (err) {
            console.error("Failed to unlink file", err);
        }
    }

    async function handleAddHyperlink() {
        if (!hyperlinkUrl.trim() || !hyperlinkTitle.trim()) return;
        setAddingLink(true);
        try {
            await filesService.addHyperlinkToVOD(mediaId, hyperlinkUrl.trim(), hyperlinkTitle.trim());
            setHyperlinkUrl("");
            setHyperlinkTitle("");
            await loadAttachments();
        } catch (err) {
            console.error("Failed to add hyperlink", err);
        } finally {
            setAddingLink(false);
        }
    }

    async function handleRemoveHyperlink(linkId: string) {
        try {
            await filesService.removeHyperlinkFromVOD(mediaId, linkId);
            await loadAttachments();
        } catch (err) {
            console.error("Failed to remove hyperlink", err);
        }
    }

    function formatFileSize(bytes?: number): string {
        if (!bytes) return "";
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
    }

    return (
        <Styled.GenerateModal show={show} onHide={onHide} size="lg" centered>
            <Styled.ModalWrapper>
                <Styled.ModalTopRow>
                    <Styled.ModalTitleGroup>
                        <Styled.ModalTitle>Attach Files & Links</Styled.ModalTitle>
                        <Styled.ModalDescription>
                            Link files from your file manager or add external hyperlinks to this video.
                        </Styled.ModalDescription>
                    </Styled.ModalTitleGroup>
                    <Styled.CloseBtn onClick={onHide} aria-label="Close">
                        <X size={22} />
                    </Styled.CloseBtn>
                </Styled.ModalTopRow>

                {loading ? (
                    <Styled.LoadingWrapper>
                        <Spinner animation="border" />
                    </Styled.LoadingWrapper>
                ) : (
                    <Styled.AttachTabs defaultActiveKey="files" id="vod-links-tabs" fill>
                        <Tab eventKey="files" title="Files">
                            <Styled.SectionBlock>
                                <Styled.SectionTitle>Linked Files</Styled.SectionTitle>
                                {linkedFiles.length === 0 ? (
                                    <Styled.EmptyText>No files linked yet.</Styled.EmptyText>
                                ) : (
                                    linkedFiles.map((file) => (
                                        <Styled.FileRow key={file.fileId}>
                                            <Styled.FileRowLeft>
                                                <FileEarmark size={18} color="#888" />
                                                <Styled.FileRowInfo>
                                                    <Styled.FileRowName>
                                                        {file.originalFilename}
                                                    </Styled.FileRowName>
                                                    {file.objectSize && (
                                                        <Styled.FileRowMeta>
                                                            {formatFileSize(file.objectSize)}
                                                        </Styled.FileRowMeta>
                                                    )}
                                                </Styled.FileRowInfo>
                                            </Styled.FileRowLeft>
                                            <Styled.UnlinkBtn onClick={() => handleUnlinkFile(file.fileId!)}>
                                                Unlink
                                            </Styled.UnlinkBtn>
                                        </Styled.FileRow>
                                    ))
                                )}
                            </Styled.SectionBlock>

                            <Styled.Divider />

                            <Styled.SectionBlock>
                                <Styled.SectionTitle>Search Files to Link</Styled.SectionTitle>
                                <Styled.SearchWrapper>
                                    <Styled.SearchIconWrapper>
                                        <Search size={13} />
                                    </Styled.SearchIconWrapper>
                                    <Styled.SearchInput
                                        type="text"
                                        placeholder="Type a file name..."
                                        value={fileSearchQuery}
                                        onChange={handleFileSearch}
                                    />
                                </Styled.SearchWrapper>

                                {fileSearching ? (
                                    <Styled.LoadingWrapper style={{ padding: "24px" }}>
                                        <Spinner animation="border" size="sm" />
                                    </Styled.LoadingWrapper>
                                ) : fileSearchQuery.trim() && fileSearchResults.length === 0 ? (
                                    <Styled.EmptyText>No files match your search.</Styled.EmptyText>
                                ) : (
                                    fileSearchResults.map((file) => {
                                        const isLinked = linkedFileIds.has(file.fileId);
                                        return (
                                            <Styled.FileRow key={file.fileId}>
                                                <Styled.FileRowLeft>
                                                    <FileEarmark size={18} color="#888" />
                                                    <Styled.FileRowInfo>
                                                        <Styled.FileRowName>
                                                            {file.originalFilename}
                                                        </Styled.FileRowName>
                                                        {file.objectSize && (
                                                            <Styled.FileRowMeta>
                                                                {formatFileSize(file.objectSize)}
                                                            </Styled.FileRowMeta>
                                                        )}
                                                    </Styled.FileRowInfo>
                                                </Styled.FileRowLeft>
                                                {isLinked ? (
                                                    <Styled.UnlinkBtn onClick={() => handleUnlinkFile(file.fileId!)}>
                                                        Unlink
                                                    </Styled.UnlinkBtn>
                                                ) : (
                                                    <Styled.LinkBtn onClick={() => handleLinkFile(file.fileId!)}>
                                                        Link
                                                    </Styled.LinkBtn>
                                                )}
                                            </Styled.FileRow>
                                        );
                                    })
                                )}
                            </Styled.SectionBlock>
                        </Tab>

                        <Tab eventKey="hyperlinks" title="Hyperlinks">
                            <Styled.SectionBlock>
                                <Styled.SectionTitle>Add Hyperlink</Styled.SectionTitle>
                                <Styled.FormGroup>
                                    <Styled.FormLabel>Title</Styled.FormLabel>
                                    <Styled.FormInput
                                        type="text"
                                        placeholder="e.g. Official website"
                                        value={hyperlinkTitle}
                                        onChange={(e) => setHyperlinkTitle(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && handleAddHyperlink()}
                                    />
                                </Styled.FormGroup>
                                <Styled.FormGroup>
                                    <Styled.FormLabel>URL</Styled.FormLabel>
                                    <Styled.FormInput
                                        type="url"
                                        placeholder="https://..."
                                        value={hyperlinkUrl}
                                        onChange={(e) => setHyperlinkUrl(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && handleAddHyperlink()}
                                    />
                                </Styled.FormGroup>
                                <Styled.AddHyperlinkBtn
                                    disabled={addingLink || !hyperlinkUrl.trim() || !hyperlinkTitle.trim()}
                                    onClick={handleAddHyperlink}
                                >
                                    {addingLink ? <Spinner animation="border" size="sm" /> : "Add Hyperlink"}
                                </Styled.AddHyperlinkBtn>
                            </Styled.SectionBlock>

                            <Styled.Divider />

                            <Styled.SectionBlock>
                                <Styled.SectionTitle>Linked Hyperlinks</Styled.SectionTitle>
                                {linkedHyperlinks.length === 0 ? (
                                    <Styled.EmptyText>No hyperlinks linked yet.</Styled.EmptyText>
                                ) : (
                                    linkedHyperlinks.map((link) => (
                                        <Styled.HyperlinkRow key={link.hyperLinkId}>
                                            <Styled.HyperlinkInfo>
                                                <Styled.HyperlinkTitle>{link.title}</Styled.HyperlinkTitle>
                                                <Styled.HyperlinkUrl
                                                    href={link.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {link.url}
                                                </Styled.HyperlinkUrl>
                                            </Styled.HyperlinkInfo>
                                            <Styled.UnlinkBtn onClick={() => handleRemoveHyperlink(link.hyperLinkId)}>
                                                Remove
                                            </Styled.UnlinkBtn>
                                        </Styled.HyperlinkRow>
                                    ))
                                )}
                            </Styled.SectionBlock>
                        </Tab>
                    </Styled.AttachTabs>
                )}
            </Styled.ModalWrapper>
        </Styled.GenerateModal>
    );
};
